import { DateTime } from 'luxon';

// Threshold in minutes for considering a signal as "now" (on the water)
const NOW_THRESHOLD_MINUTES = 2;

/**
 * Parse a timestamp string into a DateTime, trying multiple formats.
 */
const parseTimestamp = (timestamp: string): DateTime => {
  const trimmed = timestamp.trim();

  // Try ISO format first
  let date = DateTime.fromISO(trimmed);
  if (date.isValid) return date;

  // Try SQL format (e.g., "2024-01-22 10:30:00")
  date = DateTime.fromSQL(trimmed);
  if (date.isValid) return date;

  // Try RFC2822
  date = DateTime.fromRFC2822(trimmed);
  if (date.isValid) return date;

  // Return invalid DateTime if nothing works
  return DateTime.invalid('unparseable');
};

/**
 * Check if a timestamp is considered "now" (within the threshold).
 * Used for both displaying "Now" text and sorting by on-the-water status.
 */
export const isTimestampNow = (timestamp?: string): boolean => {
  if (!timestamp) return false;
  const date = parseTimestamp(timestamp);
  if (!date.isValid) return false;
  return DateTime.now().diff(date, 'minutes').minutes < NOW_THRESHOLD_MINUTES;
};

const formatTimeAgo = (date: DateTime): string | null => {
  // Handle invalid dates
  if (!date.isValid) {
    return null;
  }

  // Using luxon, if the date is more than 1 week ago, then return the full date
  if (DateTime.now().diff(date, 'days').days >= 7) {
    return date.toFormat('dd/MM/yyyy').toLowerCase();
  }

  // If the date is more than one day ago, then return the name of the day
  if (DateTime.now().diff(date, 'days').days >= 1) {
    return date.toLocaleString({ weekday: 'long' });
  }

  // if the date is more than the threshold, then return the time in 12 hour format
  if (DateTime.now().diff(date, 'minutes').minutes >= NOW_THRESHOLD_MINUTES) {
    return date.toFormat('h:mma').toLowerCase();
  }

  return 'Now';
};

export const useRelativeTimeDetailed = (timestamp: string) => {
  const date = timestamp
    ? parseTimestamp(timestamp)
    : DateTime.invalid('empty');
  const lastKnownRelativeTime = formatTimeAgo(date);
  const isNow = isTimestampNow(timestamp);

  return { lastKnownRelativeTime, isNow };
};

const useRelativeTime = (timestamp: string): string | null => {
  const { lastKnownRelativeTime } = useRelativeTimeDetailed(timestamp);
  return lastKnownRelativeTime;
};

export default useRelativeTime;
